import React, { useState, useEffect } from "react";
import axiosInstance from "../../../axiosConfig";
import { Await } from "react-router-dom";
import { ImageIcon, Download, X, Plus, Trash2, Info, ChevronDown } from "lucide-react";

export default function MerchantForm({ isEditMode, initialEntity, entityUuid, setEntityUuid, entityId, setEntityId, setEntityName, setEntityType, setEntityCode, setActiveTab }) {
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)
  const [isViewMode, setIsViewMode] = useState(false) // Track if form should be disabled
  const [metaData, setMetaData] = useState({
    entityTypes: []
  })
  const [logoPreview, setLogoPreview] = useState(null)
  const [logoFile, setLogoFile] = useState(null)
  
  // Flag to control logo upload functionality
  const [isLogoUploadEnabled, setIsLogoUploadEnabled] = useState(false) // Set to false to disable API integration
  
  const [formData, setFormData] = useState({
    name: "",
    entityTypeCode: "",
    description: "", 
    entityCode: "", 
    logo: null,
    geoLocation: {
      name: "",
      latitude: "",
      longitude: "",
      altitude: "", 
      address: "" 
    },
    fullAddress: "", 
    website: "", 
    contactNos: [''], 
    emails: [''],
   
    entityBranches: [ 
      {
        name: "",
        branchCode: "",  
        geoLocation: {
          name: "", 
          latitude: "",
          longitude: "",
          altitude: "", 
          address: "" 
        },
        fullAddress: "", 
        website: "", 
        contactNos: [], 
        emails: [] 
      }
    ]
  });

  useEffect(() => {
    const fetchMetaData = async () => {
      try {
        console.log("Fetching metadata...");
        const token = localStorage.getItem('token');
        console.log(token);

        const response = await axiosInstance.get(
          '/internal-api/v1/client-web/entity-panel/registration/meta',
        );

        if (response.data && response.data.result) {
          setMetaData(response.data.result);
          console.log("entityTypes:", response.data.result);
        }

        console.log("Response:", response.data);
      } catch (error) {
        console.error("Error fetching metadata:", error);
        setError(error.message || "Failed to fetch data");
      } finally {
        setIsLoading(false); // Set loading to false when fetch completes
      }
    };

    fetchMetaData();
  }, []);

  // Fetch entity data when returning to this tab
  useEffect(() => {
    const fetchEntityData = async () => {
      if (entityId || entityUuid) {
        try {
          console.log('Fetching entity data for UUID:', entityUuid, 'ID:', entityId);
          
          // Fetch all entities from the list API
          const response = await axiosInstance.get(
            '/internal-api/v1/client-web/entity-panel/config/list'
          );
          
          console.log('Entity list response:', response.data);
          
          if (response.data && response.data.data) {
            // Find the current entity by ID or UUID
            const currentEntity = response.data.data.find(entity => 
              entity.id === entityId || entity.uuid === entityUuid
            );
            
            if (currentEntity) {
              console.log('Found current entity:', currentEntity);
              
              const decodedWebsiteFromAPI = currentEntity.website ? decodeURIComponent(currentEntity.website) : '';
              
              // Map API response fields to form fields
              // API returns: entityCode (type code), registration (reg number)
              setFormData(prevData => ({
                ...prevData,
                name: currentEntity.name || prevData.name,
                entityCode: currentEntity.registration || prevData.entityCode,
                entityTypeCode: currentEntity.entityCode || prevData.entityTypeCode,
                website: decodedWebsiteFromAPI || prevData.website,
                description: currentEntity.description || prevData.description,
                tags: currentEntity.tags || prevData.tags,
                geoLocation: currentEntity.geoLocation || prevData.geoLocation,
                fullAddress: currentEntity.fullAddress || prevData.fullAddress,
                contactNos: currentEntity.contactNos || prevData.contactNos,
                emails: currentEntity.emails || prevData.emails
              }));
              
              // In edit mode, don't set view mode - allow editing
              // setIsViewMode(true); // Removed to allow editing
            } else {
              console.log('Entity not found in list, using existing form data');
              // If entity not found but form has data, just enable view mode
              if (formData.name && formData.entityCode && !isEditMode) {
                setIsViewMode(true);
              }
            }
          }
        } catch (error) {
          console.error('Error fetching entity data:', error);
          console.log('Error status:', error.response?.status);
          console.log('Error data:', error.response?.data);
          // If fetch fails but form has data, enable view mode only if not in edit mode
          if (formData.name && formData.entityCode && !isEditMode) {
            console.log('Using existing form data, enabling view mode');
            setIsViewMode(true);
          }
        }
      }
    };
    
    fetchEntityData();
  }, [entityId, entityUuid]);

  useEffect(() => {
    if (!isEditMode || !initialEntity) return;

    const decodedWebsiteFromInitial = initialEntity.website ? decodeURIComponent(initialEntity.website) : '';
    
    const nextGeo = initialEntity.geoLocation || {};
    const nextContactNos = Array.isArray(initialEntity.contactNos) && initialEntity.contactNos.length
      ? initialEntity.contactNos
      : [''];
    const nextEmails = Array.isArray(initialEntity.emails) && initialEntity.emails.length
      ? initialEntity.emails
      : [''];

    setFormData(prevData => ({
      ...prevData,
      name: initialEntity.name || prevData.name,
      entityCode: initialEntity.registration || initialEntity.entityCode || prevData.entityCode,
      entityTypeCode: initialEntity.entityTypeCode || initialEntity.entityType || prevData.entityTypeCode,
      website: decodedWebsiteFromInitial || prevData.website,
      description: initialEntity.description || prevData.description,
      tags: initialEntity.tags || prevData.tags,
      fullAddress: initialEntity.fullAddress || prevData.fullAddress,
      geoLocation: {
        ...prevData.geoLocation,
        name: nextGeo.name || prevData.geoLocation.name,
        latitude: nextGeo.latitude ?? prevData.geoLocation.latitude,
        longitude: nextGeo.longitude ?? prevData.geoLocation.longitude,
        altitude: nextGeo.altitude ?? prevData.geoLocation.altitude,
        address: nextGeo.address || prevData.geoLocation.address,
      },
      contactNos: nextContactNos,
      emails: nextEmails
    }));
  }, [isEditMode, initialEntity]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value
    }));
  };

  const handleGeoLocationChange = (field, value) => {
    setFormData(prevData => ({
      ...prevData,
      geoLocation: {
        ...prevData.geoLocation,
        [field]: value
      }
    }));
  };

  const handleContactNumberChange = (index, value) => {
    const updatedContacts = [...formData.contactNos];
    updatedContacts[index] = value;
    setFormData(prevData => ({
      ...prevData,
      contactNos: updatedContacts
    }));
  };

  const handleAddContactNumber = () => {
    setFormData(prevData => ({
      ...prevData,
      contactNos: [...prevData.contactNos, '']
    }));
  };

  const handleRemoveContactNumber = (index) => {
    const updatedContacts = formData.contactNos.filter((_, i) => i !== index);
    setFormData(prevData => ({
      ...prevData,
      contactNos: updatedContacts
    }));
  };

  const handleEmailChange = (index, value) => {
    const updatedEmails = [...formData.emails];
    updatedEmails[index] = value;
    setFormData(prevData => ({
      ...prevData,
      emails: updatedEmails
    }));
  };

  const handleAddEmail = () => {
    setFormData(prevData => ({
      ...prevData,
      emails: [...prevData.emails, '']
    }));
  };

  const handleRemoveEmail = (index) => {
    const updatedEmails = formData.emails.filter((_, i) => i !== index);
    setFormData(prevData => ({
      ...prevData,
      emails: updatedEmails
    }));
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
      if (!allowedTypes.includes(file.type)) {
        alert('Please select a valid image file (JPEG, PNG, GIF, or WebP)');
        return;
      }
      
      // Validate file size (5MB limit)
      const maxSize = 5 * 1024 * 1024; // 5MB in bytes
      if (file.size > maxSize) {
        alert('File size must be less than 5MB');
        return;
      }

      setLogoFile(file);
      
      // Only set logo in formData if upload is enabled
      if (isLogoUploadEnabled) {
        setFormData(prev => ({
          ...prev,
          logo: file
        }));
      }

      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setLogoPreview(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveLogo = () => {
    setLogoFile(null);
    setLogoPreview(null);
    if (isLogoUploadEnabled) {
      setFormData(prev => ({
        ...prev,
        logo: null
      }));
    }
    // Reset the file input
    const fileInput = document.querySelector('input[type="file"][name="logo"]');
    if (fileInput) {
      fileInput.value = '';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
     
    try {
      let response;
      
      // Check if we're in edit mode
      if (isEditMode && entityId) {
        // UPDATE MODE - use PUT endpoint
        const updateData = {
          id: entityId,
          name: formData.name,
          entityTypeCode: formData.entityTypeCode,
          description: formData.description || undefined,
          entityCode: formData.entityCode || undefined,
          geoLocation: {
            name: formData.geoLocation.name || undefined,
            latitude: formData.geoLocation.latitude,
            longitude: formData.geoLocation.longitude,
            altitude: formData.geoLocation.altitude || undefined,
            address: formData.geoLocation.address || undefined,
          },
          fullAddress: formData.fullAddress || undefined,
          website: formData.website || undefined,
          contactNos: formData.contactNos.filter(contact => contact.trim() !== ''),
          emails: formData.emails.filter(email => email.trim() !== ''),
        };

        response = await axiosInstance.put(
          '/internal-api/v1/client-web/entity-panel/registration/update-entity',
          updateData,
          {
            headers: {
              'Content-Type': 'application/json',
            },
          }
        );
      } else if (isLogoUploadEnabled && logoFile) {
        // CREATE MODE - Create FormData for file upload when logo upload is enabled
        const submitData = new FormData();
        
        // Append all form fields except the logo
        Object.keys(formData).forEach(key => {
          if (key !== 'logo' && key !== 'geoLocation' && key !== 'entityBranches') {
            submitData.append(key, formData[key]);
          }
        });

        // Handle nested objects
        submitData.append('geoLocation', JSON.stringify(formData.geoLocation));
        submitData.append('entityBranches', JSON.stringify(formData.entityBranches));

        // Append logo file
        submitData.append('logo', logoFile);

        response = await axiosInstance.post(
          '/internal-api/v1/client-web/entity-panel/registration/new-entity',
          submitData,
          {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          }
        );
      } else {
        // CREATE MODE - Send JSON data without logo when upload is disabled or no file selected
        const submitData = {
          ...formData,
          logo: undefined // Remove logo field completely
        };
        
        // Remove logo field from the object
        delete submitData.logo;

        response = await axiosInstance.post(
          '/internal-api/v1/client-web/entity-panel/registration/new-entity',
          submitData,
          {
            headers: {
              'Content-Type': 'application/json',
            },
          }
        );
      }

      console.log("Response:", response.data);
      
      if (isEditMode) {
        // UPDATE MODE - just show success message
        if (response.data && response.data.status) {
          console.log("Entity updated successfully");
          alert('Merchant updated successfully!');
          
          // Navigate to the next tab (location/branch)
          setActiveTab("location");
        } else {
          console.error("Error updating entity:", response);
          alert('Error updating merchant. Please try again.');
        }
      } else {
        // CREATE MODE - Check for result field (POST endpoint response)
        if (response.data && response.data.result) {
          // For create mode: Set the UUID
          setEntityUuid(response.data.result.uuid);
          
          // Set the entity ID if available in response
          if (response.data.result.id) {
            setEntityId(response.data.result.id);
          }
          
          // Set entity name from response
          if (response.data.result.name && setEntityName) {
            setEntityName(response.data.result.name);
          }
          
          // Set entity type from form data
          if (formData.entityTypeCode && setEntityType) {
            // Find the entity type name from metaData
            const selectedType = metaData.entityTypes.find(type => type.code === formData.entityTypeCode);
            setEntityType(selectedType ? selectedType.name : formData.entityTypeCode);
          }
          
          // Set entity code (registration number) from response
          if (response.data.result.code && setEntityCode) {
            setEntityCode(response.data.result.code);
          }
          
          console.log("Form submitted successfully:", response.data.result);
          console.log("Entity UUID:", response.data.result.uuid);
          console.log("Entity ID:", response.data.result.id);
          console.log("Entity Name:", response.data.result.name);
          console.log("Entity Code:", response.data.result.code);
          
          // Set view mode to disable fields after successful submission
          setIsViewMode(true);
          
          // Navigate to the next tab (location/branch)
          setActiveTab("location");
        } else {
          console.error("Error submitting form:", response);
          alert('Error creating merchant. Please try again.');
        }
      }
      
    } catch (error) {
      console.error("Error submitting form:", error);
      alert('Error creating merchant. Please try again.');
      // Optionally show error message to user
    }
  };

  const handleCancel = () => {
    console.log("Form cancelled");
    // Reset form or navigate away
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center gap-2 py-16 text-sm text-slate-500">
        <div className="w-4 h-4 border-b-2 rounded-full animate-spin border-brand-blue" />
        Loading metadata...
      </div>
    );
  }

  return (
    <div>
      {/* Form Content */}
      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Logo Upload Section */}
        <div className="pb-8 border-b border-slate-200">
          <label className="flex items-center gap-2 mb-3 text-sm font-semibold text-slate-800">
            Merchant Logo
            {!isLogoUploadEnabled && (
              <span className="px-2 py-0.5 rounded-full bg-amber-50 text-amber-600 text-xs font-medium">
                Coming soon — preview only
              </span>
            )}
          </label>
          <div className="flex flex-col items-start gap-5 sm:flex-row">
            {/* Logo Preview Area */}
            <div className="flex-shrink-0">
              <div className={`w-28 h-28 border-2 border-dashed rounded-xl flex items-center justify-center bg-slate-50 overflow-hidden ${
                !isLogoUploadEnabled ? 'border-slate-200 opacity-60' : 'border-slate-300'
              }`}>
                {logoPreview ? (
                  <img
                    src={logoPreview}
                    alt="Logo preview"
                    className="object-contain w-full h-full"
                  />
                ) : (
                  <div className="text-center">
                    <ImageIcon className="w-7 h-7 mx-auto mb-1.5 text-slate-300" />
                    <p className="text-[11px] text-slate-400">No logo</p>
                  </div>
                )}
              </div>
            </div>

            {/* Upload Controls */}
            <div className="flex-grow">
              <div className="space-y-3">
                <div>
                  <input
                    type="file"
                    id="logo"
                    name="logo"
                    accept="image/*"
                    onChange={handleLogoChange}
                    className="hidden"
                    disabled={!isLogoUploadEnabled}
                  />
                  <label
                    htmlFor={isLogoUploadEnabled ? "logo" : ""}
                    className={`inline-flex items-center gap-2 px-4 py-2 border rounded-lg text-xs font-semibold transition-colors ${
                      isLogoUploadEnabled
                        ? 'border-slate-300 text-slate-700 bg-white hover:bg-slate-50 cursor-pointer'
                        : 'border-slate-200 text-slate-400 bg-slate-50 cursor-not-allowed'
                    }`}
                  >
                    <Download className="w-3.5 h-3.5" />
                    Choose Logo
                  </label>
                  {logoFile && (
                    <button
                      type="button"
                      onClick={handleRemoveLogo}
                      className="inline-flex items-center gap-1.5 px-3 py-2 ml-3 text-xs font-semibold text-red-600 bg-white border border-red-200 rounded-lg hover:bg-red-50"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                      Remove
                    </button>
                  )}
                </div>
                <div className="text-xs text-slate-500 space-y-0.5">
                  <p>Supported formats: JPEG, PNG, GIF, WebP</p>
                  <p>Maximum file size: 5MB</p>
                  <p>Recommended dimensions: 200x200px or larger</p>
                  {!isLogoUploadEnabled && (
                    <p className="flex items-center gap-1 mt-2 text-amber-600">
                      <Info className="w-3.5 h-3.5" />
                      Logo upload functionality will be available in a future update
                    </p>
                  )}
                </div>
                {logoFile && (
                  <div className="text-xs text-emerald-600">
                    Selected: {logoFile.name} ({(logoFile.size / 1024 / 1024).toFixed(2)} MB)
                    <span className="ml-2 text-brand-blue">(Saved locally - not sent to server yet)</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
          {/* Merchant Name */}
          <div className="flex flex-col">
            <label className="mb-1.5 text-xs font-semibold text-slate-700">
              Merchant Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="Enter merchant name"
              className="px-3 py-2.5 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-blue/30 focus:border-brand-blue disabled:bg-slate-100 disabled:cursor-not-allowed"
              required
              disabled={isViewMode}
            />
          </div>

          {/* Registration Number */}
          <div className="flex flex-col">
            <label className="mb-1.5 text-xs font-semibold text-slate-700">
              Registration Number <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="entityCode"
              value={formData.entityCode}
              onChange={handleChange}
              placeholder="Enter business registration number"
              className="px-3 py-2.5 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-blue/30 focus:border-brand-blue disabled:bg-slate-100 disabled:cursor-not-allowed"
              required
              disabled={isViewMode}
            />
          </div>

          {/* Entity Type */}
          <div className="flex flex-col">
            <label className="mb-1.5 text-xs font-semibold text-slate-700">
              Entity Type <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <select
                name="entityTypeCode"
                value={formData.entityTypeCode}
                onChange={handleChange}
                className="w-full px-3 py-2.5 text-sm bg-white border border-slate-300 rounded-lg appearance-none focus:outline-none focus:ring-2 focus:ring-brand-blue/30 focus:border-brand-blue disabled:bg-slate-100 disabled:cursor-not-allowed"
                required
                disabled={isViewMode}
              >
                <option value="">Select entity type</option>
                {metaData.entityTypes.map((type) => (
                  <option key={type.id} value={type.code}>{type.name}</option>
                ))}
              </select>
              <div className="absolute inset-y-0 right-0 flex items-center px-3 pointer-events-none">
                <ChevronDown className="w-4 h-4 text-slate-400" />
              </div>
            </div>
          </div>

          {/* Website */}
          <div className="flex flex-col">
            <label className="mb-1.5 text-xs font-semibold text-slate-700">
              Website
            </label>
            <input
              type="url"
              name="website"
              value={formData.website}
              onChange={handleChange}
              placeholder="https://"
              className="px-3 py-2.5 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-blue/30 focus:border-brand-blue disabled:bg-slate-100 disabled:cursor-not-allowed"
              disabled={isViewMode}
            />
          </div>
        </div>

        {/* Description */}
        <div>
          <label className="mb-1.5 block text-xs font-semibold text-slate-700">
            Description
          </label>
          <textarea
            rows="4"
            name="description"
            value={formData.description}
            onChange={handleChange}
            placeholder="Enter detailed description of the merchant"
            className="w-full px-3 py-2.5 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-blue/30 focus:border-brand-blue disabled:bg-slate-100 disabled:cursor-not-allowed"
            disabled={isViewMode}
          ></textarea>
        </div>

          {/* Tags */}
          {/* <div className="mt-6">
            <label className="mb-1 text-xs font-medium text-gray-700">
              Tags
            </label>
            <div>
              <label className="text-xs text-gray-600">Merchant Tags</label>
              <input
                type="text"
                name="tags"
                value={formData.tags}
                onChange={handleChange}
                placeholder="Enter tags separated by commas (e.g. hotel, tour operator, transport)"
                className="w-full p-2 text-xs border rounded focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                disabled={isViewMode}
              />
            </div>
          </div> */}

          {/* Geo Location Section */}
          {/* <div className="mt-6">
            <label className="block mb-3 text-sm font-medium text-gray-700">
              Location Information
            </label>
            <div className="grid grid-cols-1 gap-6 p-4 border rounded md:grid-cols-2">
              
              <div className="flex flex-col">
                <label className="mb-1 text-xs font-medium text-gray-700">
                  Location Name
                </label>
                <input
                  type="text"
                  value={formData.geoLocation.name}
                  onChange={(e) => handleGeoLocationChange('name', e.target.value)}
                  placeholder="Enter location name"
                  className="p-2 text-xs border rounded focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                  disabled={isViewMode}
                />
              </div>

             
              <div className="flex flex-col">
                <label className="mb-1 text-xs font-medium text-gray-700">
                  Latitude <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.geoLocation.latitude}
                  onChange={(e) => handleGeoLocationChange('latitude', e.target.value)}
                  placeholder="e.g., 6.90033"
                  className="p-2 text-xs border rounded focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                  required
                  disabled={isViewMode}
                />
              </div>

             
              <div className="flex flex-col">
                <label className="mb-1 text-xs font-medium text-gray-700">
                  Longitude <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.geoLocation.longitude}
                  onChange={(e) => handleGeoLocationChange('longitude', e.target.value)}
                  placeholder="e.g., 78.476444"
                  className="p-2 text-xs border rounded focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                  required
                  disabled={isViewMode}
                />
              </div>

              
              <div className="flex flex-col">
                <label className="mb-1 text-xs font-medium text-gray-700">
                  Altitude
                </label>
                <input
                  type="text"
                  value={formData.geoLocation.altitude}
                  onChange={(e) => handleGeoLocationChange('altitude', e.target.value)}
                  placeholder="e.g., 2.32"
                  className="p-2 text-xs border rounded focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                  disabled={isViewMode}
                />
              </div>

              
              <div className="flex flex-col md:col-span-2">
                <label className="mb-1 text-xs font-medium text-gray-700">
                  Location Address
                </label>
                <input
                  type="text"
                  value={formData.geoLocation.address}
                  onChange={(e) => handleGeoLocationChange('address', e.target.value)}
                  placeholder="Enter location address"
                  className="p-2 text-xs border rounded focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                  disabled={isViewMode}
                />
              </div>
            </div>
          </div> */}

          {/* Full Address */}
          <div>
            <label className="mb-1.5 block text-xs font-semibold text-slate-700">
              Full Address
            </label>
            <textarea
              rows="2"
              name="fullAddress"
              value={formData.fullAddress}
              onChange={handleChange}
              placeholder="Enter complete address (e.g., No 2, main road, colombo)"
              className="w-full px-3 py-2.5 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-blue/30 focus:border-brand-blue disabled:bg-slate-100 disabled:cursor-not-allowed"
              disabled={isViewMode}
            ></textarea>
          </div>

          {/* Contact Information */}
          <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
            {/* Contact Numbers */}
            <div className="flex flex-col">
              <div className="flex items-center justify-between mb-2">
                <label className="text-xs font-semibold text-slate-700">
                  Contact Numbers
                </label>
                {!isViewMode && (
                  <button
                    type="button"
                    onClick={handleAddContactNumber}
                    className="flex items-center gap-1 px-2.5 py-1 text-xs font-semibold text-brand-blue border border-brand-blue/40 rounded-md hover:bg-brand-blue/5"
                  >
                    <Plus className="w-3 h-3" />
                    Add
                  </button>
                )}
              </div>
              <div className="space-y-2">
                {formData.contactNos.map((contact, index) => (
                  <div key={index} className="flex gap-2">
                    <input
                      type="tel"
                      value={contact}
                      onChange={(e) => handleContactNumberChange(index, e.target.value)}
                      placeholder="Enter contact number"
                      className="flex-1 px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-blue/30 focus:border-brand-blue disabled:bg-slate-100 disabled:cursor-not-allowed"
                      disabled={isViewMode}
                    />
                    {!isViewMode && formData.contactNos.length > 1 && (
                      <button
                        type="button"
                        onClick={() => handleRemoveContactNumber(index)}
                        className="px-2.5 py-2 text-red-500 border border-red-200 rounded-lg hover:bg-red-50"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Email Addresses */}
            <div className="flex flex-col">
              <div className="flex items-center justify-between mb-2">
                <label className="text-xs font-semibold text-slate-700">
                  Email Addresses
                </label>
                {!isViewMode && (
                  <button
                    type="button"
                    onClick={handleAddEmail}
                    className="flex items-center gap-1 px-2.5 py-1 text-xs font-semibold text-brand-blue border border-brand-blue/40 rounded-md hover:bg-brand-blue/5"
                  >
                    <Plus className="w-3 h-3" />
                    Add
                  </button>
                )}
              </div>
              <div className="space-y-2">
                {formData.emails.map((email, index) => (
                  <div key={index} className="flex gap-2">
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => handleEmailChange(index, e.target.value)}
                      placeholder="Enter email address"
                      className="flex-1 px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-blue/30 focus:border-brand-blue disabled:bg-slate-100 disabled:cursor-not-allowed"
                      disabled={isViewMode}
                    />
                    {!isViewMode && formData.emails.length > 1 && (
                      <button
                        type="button"
                        onClick={() => handleRemoveEmail(index)}
                        className="px-2.5 py-2 text-red-500 border border-red-200 rounded-lg hover:bg-red-50"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Branch Management */}
          {/* <div className="mt-6">
            <label className="mb-1 text-sm font-medium text-gray-700">
              Branch Management
            </label>
            <div className="p-6 border rounded">
              <div className="p-4 text-center rounded bg-blue-50">
                <h3 className="mb-2 text-sm font-medium text-gray-700">Branches Management</h3>
                <p className="text-xs text-gray-600">After saving the merchant, you will be able to add branch locations with:</p>
                <p className="mt-2 text-xs text-gray-600">
                  • Branch address/location details • Contact information • Branch-specific settings
                </p>
              </div>
            </div>
          </div> */}

        {/* Required Fields Note */}
        <div className="flex flex-col-reverse items-start justify-between gap-4 pt-6 border-t border-slate-200 sm:flex-row sm:items-center">
          {isViewMode ? (
            <span className="flex items-center gap-1.5 text-xs text-brand-blue">
              <Info className="w-3.5 h-3.5" />
              Basic information has been saved and cannot be modified. Please use other tabs to complete registration.
            </span>
          ) : (
            <span className="text-xs text-slate-500">
              <span className="text-red-500">*</span> Required fields
            </span>
          )}
          <div className="flex items-center">
            {!isViewMode && (
              <>
                <button
                  type="button"
                  onClick={handleCancel}
                  className="px-4 py-2 mr-2 text-sm font-semibold border rounded-lg text-slate-600 border-slate-300 hover:bg-slate-50"
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  className="px-5 py-2 text-sm font-semibold text-white rounded-lg bg-brand-blue hover:bg-brand-blue-dark"
                >
                  {isEditMode ? 'Update & Next' : 'Next'}
                </button>
              </>
            )}
          </div>
        </div>
      </form>
    </div>
  );
}
